import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { useTheme } from "./ThemeContext";

const CompanyContext = createContext(null);

// Fallback si el GET /settings/platform aún no carga o falla. Mismo valor
// que el backend (DEFAULT_PLATFORM_COLOR) — mantener en sync si se cambia.
const FALLBACK_PLATFORM_COLOR = "#6B7280";
const FALLBACK_PLATFORM_NAME = "Gas2GO";

function hexContrast(hex) {
  if (!hex) return "#ffffff";
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 160 ? "#000000" : "#ffffff";
}

/** Luminancia 0..1 (sRGB simplified). Útil para detectar colores muy
 *  oscuros que se confundirían con el fondo en dark mode. */
function luminance(hex) {
  if (!hex) return 0;
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;
  return (0.299 * r + 0.587 * g + 0.114 * b);
}

/** Si la primary es muy oscura (tipo #0A0A0A) se confunde con el fondo
 *  dark. Subimos el brillo a un gris claro manteniendo el look
 *  monocromático. Empresas con marca propia no se tocan. */
function effectivePrimary(hex, isDark) {
  if (!isDark) return hex;
  const lum = luminance(hex);
  if (lum < 0.12) return "#f5f5f5";
  return hex;
}

export function CompanyProvider({ children }) {
  const { user } = useAuth() || {};
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [companies, setCompanies] = useState([]);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [platformColor, setPlatformColor] = useState(FALLBACK_PLATFORM_COLOR);
  const [heroImageUrl, setHeroImageUrl] = useState(null);
  const [heroImageUrlDark, setHeroImageUrlDark] = useState(null);
  const [faviconUrl, setFaviconUrl] = useState(null);
  const [loginBannerImage, setLoginBannerImage] = useState(null);
  const [platformName, setPlatformName] = useState(FALLBACK_PLATFORM_NAME);
  const [landingHeadingLine1, setLandingHeadingLine1] = useState("Tu gas,");
  const [landingHeadingLine2, setLandingHeadingLine2] = useState("cuando lo necesites.");
  const [landingChips, setLandingChips] = useState([]);

  // Color/hero/nombre de plataforma (gestionados por super_admin). Carga
  // pública: landing/login/register también los necesitan para no
  // mostrar valores de una empresa específica antes del login. Failsafe
  // a defaults si el endpoint aún no responde.
  const loadPlatformSettings = () =>
    api.get("/settings/platform")
      .then(({ data }) => {
        setPlatformColor(data?.primary_color || FALLBACK_PLATFORM_COLOR);
        setHeroImageUrl(data?.hero_image_url || null);
        setHeroImageUrlDark(data?.hero_image_url_dark || null);
        setFaviconUrl(data?.favicon_url || null);
        setLoginBannerImage(data?.login_banner_image || null);
        setPlatformName(data?.platform_name || FALLBACK_PLATFORM_NAME);
        setLandingHeadingLine1(data?.landing_heading_line1 || "Tu gas,");
        setLandingHeadingLine2(data?.landing_heading_line2 || "cuando lo necesites.");
        setLandingChips(Array.isArray(data?.landing_chips) ? data.landing_chips : []);
      })
      .catch(() => {});
  useEffect(() => { loadPlatformSettings(); }, []);

  useEffect(() => {
    api.get("/companies").then(({ data }) => setCompanies(data)).catch(() => {});
  }, []);

  // Refresca empresas cuando cambia el usuario (ej. admin recién logueado).
  useEffect(() => {
    if (!user) return;
    api.get("/companies").then(({ data }) => setCompanies(data)).catch(() => {});
  }, [user?.user_id]);

  useEffect(() => {
    // Sin usuario o en landing/login/register: NO pre-seleccionamos
    // empresa. La app arranca en "modo plataforma" (gris) hasta que el
    // cliente elija manualmente.
    if (!user) {
      setCurrentCompany(null);
      return;
    }

    // Super admin: siempre modo plataforma, nunca hereda color de tenant.
    if (user.role === "super_admin") {
      setCurrentCompany(null);
      return;
    }

    // Company admin: su propia empresa (siempre "selecciona empresa" para él).
    if (user.role === "company_admin" && user.company_id) {
      if (!companies.length) return;
      const own = companies.find((c) => c.company_id === user.company_id);
      if (own) { setCurrentCompany(own); return; }
      api.get(`/companies/${user.company_id}`).then(({ data }) => setCurrentCompany(data)).catch(() => {});
      return;
    }

    // Cliente: solo restauramos la empresa si el cliente la eligió antes
    // explícitamente. Si no hay selección guardada → arrancamos en null
    // (color plataforma) y el Home pedirá que elija.
    if (user.role === "client") {
      const saved = localStorage.getItem("selected_company_id");
      if (!saved || !companies.length) return;
      const picked = companies.find((c) => c.company_id === saved);
      if (picked) setCurrentCompany(picked);
    }
  }, [companies, user?.user_id, user?.role, user?.company_id]);

  // Aplicamos `--company-primary` en función del estado actual:
  // - sin empresa seleccionada → color plataforma (gris por default).
  // - con empresa seleccionada → color de la empresa.
  useEffect(() => {
    const baseColor = currentCompany?.primary_color || platformColor;
    const primary = effectivePrimary(baseColor, isDark);
    document.documentElement.style.setProperty("--company-primary", primary);
    document.documentElement.style.setProperty("--company-primary-contrast", hexContrast(primary));
    // Solo persistimos la selección si fue una empresa real elegida por el
    // cliente — nunca el color plataforma.
    if (currentCompany && (user?.role === "client" || !user)) {
      localStorage.setItem("selected_company_id", currentCompany.company_id);
    }
  }, [currentCompany, platformColor, user?.role, isDark]);

  // Favicon dinámico: cuando el super_admin cambia `favicon_url` queremos
  // que el ícono de la pestaña se actualice al instante, sin requerir
  // refresh. Reutilizamos el `<link rel="icon">` inyectado en index.html
  // — si no existe lo creamos. El `?t=…` busta caché en navegadores
  // que suelen cachear el favicon de manera agresiva.
  useEffect(() => {
    if (typeof document === "undefined" || !faviconUrl) return;
    let link = document.querySelector('link[rel="icon"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "icon");
      document.head.appendChild(link);
    }
    const sep = faviconUrl.includes("?") ? "&" : "?";
    link.setAttribute("href", `${faviconUrl}${sep}v=${Date.now()}`);
  }, [faviconUrl]);

  return (
    <CompanyContext.Provider value={{
      companies, setCompanies,
      currentCompany, setCurrentCompany,
      platformColor, setPlatformColor,
      heroImageUrl, setHeroImageUrl,
      heroImageUrlDark, setHeroImageUrlDark,
      faviconUrl, setFaviconUrl,
      loginBannerImage, setLoginBannerImage,
      platformName, setPlatformName,
      landingHeadingLine1, setLandingHeadingLine1,
      landingHeadingLine2, setLandingHeadingLine2,
      landingChips, setLandingChips,
      reloadPlatformSettings: loadPlatformSettings,
    }}>
      {children}
    </CompanyContext.Provider>
  );
}

export const useCompany = () => useContext(CompanyContext);
